import { useMemo, useState } from 'react';
import {
  PALETTE, CUE_KINDS, CUE_KIND_LABEL, ENSEMBLES, ENSEMBLE_LABEL, minutesToHHMM,
  type CueItem, type CueStep,
} from '@micplot/shared';
import { api } from '../lib/api';
import { useStore, usePresenters, useChannels, useCues, useCanEdit } from '../state/store';
import CueEditor from '../components/CueEditor';
import ConfirmDialog from '../components/ConfirmDialog';
import TemplatePicker from '../components/TemplatePicker';

/**
 * The running order.
 *
 * In set-list mode this is the spine of the show: the playhead walks *steps*,
 * so a number with a mic changeover partway through is one item you step
 * through twice rather than two items in the list.
 */
export default function Cues() {
  const identity = useStore(s => s.identity)!;
  const notify = useStore(s => s.notify);
  const session = useStore(s => s.state?.session);
  const presenters = usePresenters();
  const channels = useChannels();
  const cues = useCues();
  const canEdit = useCanEdit();

  const [roleFilter, setRoleFilter] = useState('all');
  const [editing, setEditing] = useState<{ cueId: string; stepId: string } | null>(null);
  const [editingMeta, setEditingMeta] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<CueItem | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);

  const pos = session?.playheadPos ?? 0;
  const mode = session?.mode ?? 'run-and-gun';
  const byId = useMemo(() => new Map(presenters.map(p => [p.id, p])), [presenters]);

  /** Flat step order, matching how the server indexes the playhead. */
  const flat = useMemo(() => {
    const out: { cue: CueItem; step: CueStep; index: number }[] = [];
    for (const c of cues) for (const st of c.steps) out.push({ cue: c, step: st, index: out.length });
    return out;
  }, [cues]);

  const roles = useMemo(() => {
    const set = new Set(cues.map(c => c.roleTag).filter(Boolean) as string[]);
    return ['all', ...[...set].sort()];
  }, [cues]);

  const shown = roleFilter === 'all' ? cues : cues.filter(c => c.roleTag === roleFilter);

  const run = async (fn: () => Promise<unknown>) => {
    try { await fn(); } catch (e) { notify((e as Error).message, 'error'); }
  };

  const here = flat[pos];

  return (
    <div className="panel col" style={{ height: '100%' }}>
      <div className="panel-head">
        <span className="panel-title">Running order</span>
        <span className="count">
          {flat.length ? `${pos + 1} / ${flat.length}` : '0'}
        </span>
        {here && here.cue.steps.length > 1 && (
          <span className="chip">
            change {here.step.position + 1} of {here.cue.steps.length}
          </span>
        )}
        <div className="header-spacer" />
        {roles.length > 1 && (
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                  style={{ width: 130 }} aria-label="Filter by role">
            {roles.map(r => <option key={r} value={r}>{r === 'all' ? 'All roles' : r}</option>)}
          </select>
        )}
        <button className="btn sm" disabled={!canEdit || pos <= 0}
                onClick={() => run(() => api.playhead(identity.code, 'prev'))}>◀ Prev</button>
        <button className="btn sm" disabled={!canEdit || pos >= flat.length - 1}
                onClick={() => run(() => api.playhead(identity.code, 'next'))}>Next ▶</button>
        <button className="btn sm primary" disabled={!canEdit}
                onClick={() => run(async () => {
                  await api.playhead(identity.code, 'deploy');
                  notify('Deployed.');
                })}>Deploy</button>
      </div>

      {canEdit && (
        <form className="cue-add"
              onSubmit={e => {
                e.preventDefault();
                const label = newLabel.trim();
                if (!label) return;
                setNewLabel('');
                void run(() => api.addCue(identity.code, { label }));
              }}>
          <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
                 placeholder="Add an item — e.g. Act I sc.1, or Welcome"
                 aria-label="New running order item" />
          <button className="btn primary" disabled={!newLabel.trim()}>Add</button>
          <button type="button" className="btn" onClick={() => setTemplateOpen(true)}>
            Start from a template
          </button>
        </form>
      )}

      {mode !== 'set-list' && cues.length > 0 && (
        <div className="banner" style={{ margin: '0 10px 10px' }}>
          <span>
            This show is in <strong>{mode === 'timed' ? 'timed' : 'run-and-gun'}</strong> mode,
            so the running order is a reference only. Switch to <strong>Set list</strong> in
            Session → Settings to have advancing an item change the board.
          </span>
        </div>
      )}

      <div className="col-body">
        {cues.length === 0 ? (
          <div className="empty">
            <div>No items yet.</div>
            <div style={{ fontSize: 12 }}>
              Start from a template above, add items one at a time, or import a
              rundown from the A2 board.
            </div>
          </div>
        ) : (
          <div className="cue-list">
            {shown.map(c => {
              const cueIndex = cues.indexOf(c);
              const firstFlat = flat.find(f => f.cue.id === c.id)?.index ?? 0;
              const lastFlat = firstFlat + c.steps.length - 1;
              const isCurrent = pos >= firstFlat && pos <= lastFlat;

              return (
                <div key={c.id} className={`cue-group${isCurrent ? ' current' : ''}`}>
                  <div className={`cue${pos > lastFlat ? ' past' : ''}`}>
                    <span className="cue-pos">{cueIndex + 1}</span>

                    {canEdit && (
                      <span className="cue-move">
                        <button className="btn sm" disabled={cueIndex === 0}
                                aria-label="Move up"
                                onClick={() => run(() =>
                                  api.reorderCue(identity.code, c.id, cueIndex - 1))}>▲</button>
                        <button className="btn sm" disabled={cueIndex === cues.length - 1}
                                aria-label="Move down"
                                onClick={() => run(() =>
                                  api.reorderCue(identity.code, c.id, cueIndex + 1))}>▼</button>
                      </span>
                    )}

                    <span className="cue-label">
                      <span className="row" style={{ gap: 7 }}>
                        {c.kind !== 'other' && (
                          <span className={`chip kind-${c.kind}`}>{CUE_KIND_LABEL[c.kind]}</span>
                        )}
                        {c.ensemble && (
                          <span className="chip">{ENSEMBLE_LABEL[c.ensemble]}</span>
                        )}
                        <strong>{c.label}</strong>
                      </span>
                      {c.notes && <div className="stage-meta">{c.notes}</div>}
                    </span>

                    {c.steps.length === 1 && (
                      <Rig step={c.steps[0]!} byId={byId} channels={channels} />
                    )}

                    {c.start !== undefined && (
                      <span className="cue-time">
                        {minutesToHHMM(c.start)}
                        {c.end !== undefined && `–${minutesToHHMM(c.end)}`}
                      </span>
                    )}

                    {canEdit && (
                      <>
                        <button className="btn sm" onClick={() => setEditingMeta(c.id)}>Edit</button>
                        {c.steps.length === 1 && (
                          <>
                            <button className="btn sm"
                                    onClick={() => setEditing({ cueId: c.id, stepId: c.steps[0]!.id })}>
                              Mics
                            </button>
                            <button className="btn sm" title="Put this on the board now"
                                    onClick={() => run(async () => {
                                      const r = await api.applyStep(identity.code, c.steps[0]!.id);
                                      notify(`Applied ${r.applied} assignment(s).`);
                                    })}>Apply</button>
                          </>
                        )}
                        <button className="btn sm" title="Add a mic changeover within this item"
                                onClick={() => run(() => api.addStep(identity.code, c.id))}>
                          + Change
                        </button>
                        <button className="btn sm" aria-label={`Remove ${c.label}`}
                                title="Remove this item"
                                onClick={() => setConfirmDelete(c)}>✕</button>
                      </>
                    )}
                  </div>

                  {/* Changeovers, only worth showing once there is more than one. */}
                  {c.steps.length > 1 && c.steps.map((st, i) => {
                    const flatIndex = firstFlat + i;
                    return (
                      <div key={st.id}
                           className={`cue-step${pos === flatIndex ? ' current' : ''}${pos > flatIndex ? ' past' : ''}`}>
                        <span className="cue-pos">{i + 1}</span>
                        <span className="cue-label">
                          {st.label || (i === 0 ? 'Opening state' : `Change ${i}`)}
                        </span>
                        <Rig step={st} byId={byId} channels={channels} />
                        {canEdit && (
                          <>
                            <button className="btn sm"
                                    onClick={() => setEditing({ cueId: c.id, stepId: st.id })}>
                              Mics
                            </button>
                            <button className="btn sm" onClick={() => run(async () => {
                              const r = await api.applyStep(identity.code, st.id);
                              notify(`Applied ${r.applied} assignment(s).`);
                            })}>Apply</button>
                            <button className="btn sm" aria-label="Remove this changeover"
                                    disabled={c.steps.length <= 1}
                                    onClick={() => run(() => api.removeStep(identity.code, st.id))}>
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <CueEditor
          cue={cues.find(c => c.id === editing.cueId)!}
          stepId={editing.stepId}
          channels={channels}
          presenters={presenters}
          onClose={() => setEditing(null)}
        />
      )}

      {templateOpen && <TemplatePicker onClose={() => setTemplateOpen(false)} />}

      {confirmDelete && (
        <ConfirmDialog
          title={`Remove "${confirmDelete.label}"?`}
          body="Its mic assignments go with it. The rest of the running order is renumbered."
          confirmLabel="Remove"
          danger
          onConfirm={() => {
            const c = confirmDelete;
            setConfirmDelete(null);
            void run(() => api.removeCue(identity.code, c.id));
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {editingMeta && (
        <CueMetaEditor
          cue={cues.find(c => c.id === editingMeta)!}
          onClose={() => setEditingMeta(null)}
        />
      )}
    </div>
  );
}

/** The mics a step puts up, as colour-coded chips. */
function Rig({ step, byId, channels }: {
  step: CueStep;
  byId: Map<string, any>;
  channels: { id: string; number: number }[];
}) {
  if (step.assignments.length === 0) {
    return <span className="muted" style={{ fontSize: 12 }}>no mics set</span>;
  }
  return (
    <span className="cue-rig">
      {step.assignments.map(a => {
        const p = byId.get(a.presenterId);
        const ch = channels.find(x => x.id === a.channelId);
        if (!p || !ch) return null;
        return (
          <span key={a.channelId} className="cue-slot"
                style={{ background: PALETTE[p.color % PALETTE.length] }}>
            {ch.number} {p.name}
          </span>
        );
      })}
    </span>
  );
}

/** Label, kind and ensemble for one item. */
function CueMetaEditor({ cue, onClose }: { cue: CueItem; onClose: () => void }) {
  const identity = useStore(s => s.identity)!;
  const notify = useStore(s => s.notify);
  const [label, setLabel] = useState(cue.label);
  const [kind, setKind] = useState(cue.kind);
  const [ensemble, setEnsemble] = useState(cue.ensemble ?? '');
  const [notes, setNotes] = useState(cue.notes ?? '');

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-label="Edit item">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>Running order item</h2>
          <button className="btn sm" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="field">
          <label htmlFor="clabel">Title</label>
          <input id="clabel" value={label} onChange={e => setLabel(e.target.value)} />
        </div>
        <div className="field">
          <label>Type</label>
          <div className="seg">
            {CUE_KINDS.map(k => (
              <button key={k} aria-pressed={kind === k} onClick={() => setKind(k)}>
                <strong>{CUE_KIND_LABEL[k]}</strong>
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label>Who is on</label>
          <div className="seg">
            <button aria-pressed={ensemble === ''} onClick={() => setEnsemble('')}>
              <strong>Unset</strong>
            </button>
            {ENSEMBLES.map(e => (
              <button key={e} aria-pressed={ensemble === e} onClick={() => setEnsemble(e)}>
                <strong>{ENSEMBLE_LABEL[e]}</strong>
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="cnotes">Notes</label>
          <input id="cnotes" value={notes} onChange={e => setNotes(e.target.value)}
                 placeholder="e.g. band mics hot from the top" />
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={async () => {
            try {
              await api.updateCue(identity.code, cue.id, {
                label, kind, ensemble: ensemble || '', notes,
              });
              onClose();
            } catch (e) { notify((e as Error).message, 'error'); }
          }}>Save</button>
        </div>
      </div>
    </div>
  );
}
